import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, TrendingUp, Wallet, FileText, RefreshCw, Edit2, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrackerView = () => {
  const [formData, setFormData] = useState({
    customer_name: "",
    cheque_number: "",
    cheque_amount: "",
    amount_received: "",
    payment_method: "Cash",
    payment_date: new Date().toISOString().split('T')[0],
    cheque_return_date: new Date().toISOString().split('T')[0],
    returned_to: ""
  });
  
  const [recentCheques, setRecentCheques] = useState([]);
  const [stats, setStats] = useState({
    total_cheques: 0,
    total_cheque_amount: 0,
    total_received: 0
  });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editingCheque, setEditingCheque] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchRecentCheques();
    fetchStats();
    const interval = setInterval(() => {
      fetchRecentCheques();
      fetchStats();
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const fetchRecentCheques = async () => {
    try {
      const response = await axios.get(`${API}/cheques`);
      setRecentCheques(response.data.slice(0, 5));
    } catch (error) {
      console.error("Error fetching cheques:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/cheques/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await Promise.all([fetchRecentCheques(), fetchStats()]);
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/cheques`, {
        ...formData,
        cheque_amount: parseFloat(formData.cheque_amount),
        amount_received: parseFloat(formData.amount_received)
      });

      toast.success("Cheque return recorded successfully!");
      
      setFormData({
        customer_name: "",
        cheque_number: "",
        cheque_amount: "",
        amount_received: "",
        payment_method: "Cash",
        payment_date: new Date().toISOString().split('T')[0],
        cheque_return_date: new Date().toISOString().split('T')[0],
        returned_to: ""
      });

      fetchRecentCheques();
      fetchStats();
    } catch (error) {
      console.error("Error creating cheque return:", error);
      toast.error("Failed to record cheque return");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (cheque) => {
    setEditingCheque({...cheque});
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${API}/cheques/${editingCheque.id}`, {
        ...editingCheque,
        cheque_amount: parseFloat(editingCheque.cheque_amount),
        amount_received: parseFloat(editingCheque.amount_received)
      });

      toast.success("Cheque updated successfully!");
      setShowEditModal(false);
      setEditingCheque(null);
      fetchRecentCheques();
      fetchStats();
    } catch (error) {
      console.error("Error updating cheque:", error);
      toast.error("Failed to update cheque");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/cheques/${id}`);
      toast.success("Cheque deleted successfully!");
      setDeleteConfirm(null);
      fetchRecentCheques();
      fetchStats();
    } catch (error) {
      console.error("Error deleting cheque:", error);
      toast.error("Failed to delete cheque");
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleEditChange = (e) => {
    setEditingCheque({
      ...editingCheque,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="group bg-gradient-to-br from-slate-900 to-slate-900/50 border border-slate-800 hover:border-slate-700 p-6 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-slate-900/50" data-testid="total-cheques-stat">
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-slate-800/50 rounded-lg group-hover:bg-slate-800 transition-colors">
              <FileText className="w-6 h-6 text-slate-400 group-hover:text-slate-300 transition-colors" />
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider font-medium text-slate-500">Total Cheques</p>
              <p className="text-3xl font-semibold text-slate-50 mt-1">{stats.total_cheques}</p>
            </div>
          </div>
        </div>
        
        <div className="group bg-gradient-to-br from-slate-900 to-slate-900/50 border border-slate-800 hover:border-emerald-800/50 p-6 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-emerald-900/20" data-testid="total-cheque-amount-stat">
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-emerald-900/20 rounded-lg group-hover:bg-emerald-900/30 transition-colors">
              <TrendingUp className="w-6 h-6 text-emerald-400 transition-colors" />
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider font-medium text-slate-500">Cheque Amount</p>
              <p className="text-3xl font-semibold text-slate-50 mt-1">₹{stats.total_cheque_amount.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="group bg-gradient-to-br from-slate-900 to-slate-900/50 border border-slate-800 hover:border-amber-800/50 p-6 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-amber-900/20" data-testid="total-received-stat">
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-amber-900/20 rounded-lg group-hover:bg-amber-900/30 transition-colors">
              <Wallet className="w-6 h-6 text-amber-400 transition-colors" />
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider font-medium text-slate-500">Total Received</p>
              <p className="text-3xl font-semibold text-slate-50 mt-1">₹{stats.total_received.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Add Form */}
        <div className="lg:col-span-1">
          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-50" style={{ fontFamily: 'var(--font-heading)' }}>
                Add Returned Cheque
              </h2>
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500/10 to-amber-500/10 flex items-center justify-center">
                <Plus className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                  Customer Name
                </label>
                <input
                  type="text"
                  name="customer_name"
                  data-testid="customer-name-input"
                  value={formData.customer_name}
                  onChange={handleChange}
                  required
                  placeholder="Enter customer name"
                  className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                    Cheque Number
                  </label>
                  <input
                    type="text"
                    name="cheque_number"
                    data-testid="cheque-number-input"
                    value={formData.cheque_number}
                    onChange={handleChange}
                    required
                    placeholder="CHK001"
                    className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                    Payment Method
                  </label>
                  <select
                    name="payment_method"
                    data-testid="payment-method-select"
                    value={formData.payment_method}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                  >
                    <option value="Cash">Cash</option>
                    <option value="UPI">UPI</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                    Cheque Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*\.?[0-9]*"
                      name="cheque_amount"
                      data-testid="cheque-amount-input"
                      value={formData.cheque_amount}
                      onChange={handleChange}
                      required
                      placeholder="0.00"
                      className="w-full pl-8 pr-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                    Amount Received
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*\.?[0-9]*"
                      name="amount_received"
                      data-testid="amount-received-input"
                      value={formData.amount_received}
                      onChange={handleChange}
                      required
                      placeholder="0.00"
                      className="w-full pl-8 pr-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    name="payment_date"
                    data-testid="payment-date-input"
                    value={formData.payment_date}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                    Cheque Return Date
                  </label>
                  <input
                    type="date"
                    name="cheque_return_date"
                    data-testid="cheque-return-date-input"
                    value={formData.cheque_return_date}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                  Returned To Whom
                </label>
                <input
                  type="text"
                  name="returned_to"
                  data-testid="returned-to-input"
                  value={formData.returned_to}
                  onChange={handleChange}
                  required
                  placeholder="Person who received the cheque"
                  className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                />
              </div>

              <button
                type="submit"
                data-testid="submit-cheque-btn"
                disabled={loading}
                className="w-full px-6 py-3.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-900/30 hover:shadow-emerald-900/50"
              >
                <Plus className="w-5 h-5" />
                {loading ? "Adding..." : "Add Cheque Return"}
              </button>
            </form>
          </div>
        </div>

        {/* Recent Entries Table */}
        <div className="lg:col-span-2">
          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-50" style={{ fontFamily: 'var(--font-heading)' }}>
                Recent Entries
              </h2>
              <button
                onClick={refreshData}
                data-testid="refresh-btn"
                disabled={refreshing}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 text-slate-400 hover:text-slate-300 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
            
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="w-full" data-testid="recent-cheques-table">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-400">Customer</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-400">Cheque #</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-400">Returned To</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-400">Return Date</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {recentCheques.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-12">
                        <div className="flex flex-col items-center gap-3">
                          <div className="p-4 bg-slate-800/30 rounded-full">
                            <FileText className="w-8 h-8 text-slate-600" />
                          </div>
                          <p className="text-slate-500 font-medium">No entries yet</p>
                          <p className="text-sm text-slate-600">Add your first cheque return to get started</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    recentCheques.map((cheque) => (
                      <tr key={cheque.id} className="hover:bg-slate-800/30 transition-colors" data-testid={`cheque-row-${cheque.id}`}>
                        <td className="py-4 px-4 text-slate-200 font-medium">{cheque.customer_name}</td>
                        <td className="py-4 px-4 text-slate-400 font-mono text-sm">{cheque.cheque_number}</td>
                        <td className="py-4 px-4 text-slate-300">{cheque.returned_to}</td>
                        <td className="py-4 px-4 text-slate-400 text-sm">{cheque.cheque_return_date}</td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(cheque)}
                              data-testid={`edit-btn-${cheque.id}`}
                              className="p-2 hover:bg-slate-700 rounded-lg transition-colors text-slate-400 hover:text-emerald-400"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(cheque.id)}
                              data-testid={`delete-btn-${cheque.id}`}
                              className="p-2 hover:bg-slate-700 rounded-lg transition-colors text-slate-400 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editingCheque && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="edit-modal">
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-slate-50">Edit Cheque Return</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-2">
                <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                  Customer Name
                </label>
                <input
                  type="text"
                  name="customer_name"
                  value={editingCheque.customer_name}
                  onChange={handleEditChange}
                  required
                  className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                    Cheque Number
                  </label>
                  <input
                    type="text"
                    name="cheque_number"
                    value={editingCheque.cheque_number}
                    onChange={handleEditChange}
                    required
                    className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                    Payment Method
                  </label>
                  <select
                    name="payment_method"
                    value={editingCheque.payment_method}
                    onChange={handleEditChange}
                    className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                  >
                    <option value="Cash">Cash</option>
                    <option value="UPI">UPI</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                    Cheque Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*\.?[0-9]*"
                      name="cheque_amount"
                      value={editingCheque.cheque_amount}
                      onChange={handleEditChange}
                      required
                      className="w-full pl-8 pr-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                    Amount Received
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">₹</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*\.?[0-9]*"
                      name="amount_received"
                      value={editingCheque.amount_received}
                      onChange={handleEditChange}
                      required
                      className="w-full pl-8 pr-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    name="payment_date"
                    value={editingCheque.payment_date}
                    onChange={handleEditChange}
                    required
                    className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                    Cheque Return Date
                  </label>
                  <input
                    type="date"
                    name="cheque_return_date"
                    value={editingCheque.cheque_return_date}
                    onChange={handleEditChange}
                    required
                    className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-xs uppercase tracking-wider font-medium text-slate-400">
                  Returned To Whom
                </label>
                <input
                  type="text"
                  name="returned_to"
                  value={editingCheque.returned_to}
                  onChange={handleEditChange}
                  required
                  className="w-full px-4 py-3 bg-slate-950/50 border border-slate-800 rounded-lg text-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:border-transparent transition-all duration-200"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-900/30"
                >
                  {loading ? "Updating..." : "Update Cheque"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="delete-modal">
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold text-slate-50 mb-4">Delete Cheque Return?</h2>
            <p className="text-slate-400 mb-6">Are you sure you want to delete this cheque return? This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg font-semibold transition-all duration-200 shadow-lg shadow-red-900/30"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackerView;